import type { ReactNode } from 'react'
import styles from './NotificationList.module.css'
import { Button } from '@/components/Button'
import { Avatar } from '@/components/Avatar'
import { timeago } from '@/utils/date-util'
import { Link } from '@/components/Link'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIllustration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { MinidownContent } from '@/components/MinidownContent'
import { ErrorBlock } from '@/components/ErrorBlock'
import { byDesign } from '@/infra/design'
import imgNoNotificationsUrl from '@/assets/misc/img_no-notifications.svg'
import NoNotificationsIllustration from '@/components/SpotIllustration/NoNotificationsIllustration'
import logoWorklayerBlueUrl from '@/assets/misc/logo_worklayer-blue.svg'

export interface NotificationListProps {
  children?: ReactNode
  loader?: ReactNode
}

export function NotificationList({ children, loader }: NotificationListProps) {
  return (
    <div>
      {children}
      {loader}
    </div>
  )
}

export interface NotificationListErrorProps {
  error: unknown
}

export function NotificationListError({ error }: NotificationListErrorProps) {
  return (
    <div className={styles.error}>
      <ErrorBlock error={error} />
    </div>
  )
}

export interface NotificationListEmptyProps {
  corporateMode: boolean
}

export function NotificationListEmpty({
  corporateMode,
}: NotificationListEmptyProps) {
  return (
    <EmptyState size="sm" pad>
      {!corporateMode && (
        <EmptyStateIllustration
          src={imgNoNotificationsUrl}
          illustration={byDesign(undefined, NoNotificationsIllustration)}
        />
      )}
      <EmptyStateTitle>No notifications right now</EmptyStateTitle>
      <EmptyStateDescription>
        As soon as there are any status updates, <br />
        we'll show the notifications here
      </EmptyStateDescription>
    </EmptyState>
  )
}

export interface NotificationGroupViewProps {
  isOlder: boolean
  showMarkAllAsRead: boolean
  isMarkAllAsReadPending?: boolean
  onMarkAllAsRead?: () => unknown
  children?: ReactNode
}

export function NotificationGroupView({
  isOlder,
  showMarkAllAsRead,
  isMarkAllAsReadPending,
  onMarkAllAsRead,
  children,
}: NotificationGroupViewProps) {
  return (
    <div className={styles.group}>
      <div className={styles.groupHeader}>
        <p className={styles.groupTitle}>{isOlder ? 'Older' : 'Today'}</p>
        {showMarkAllAsRead && (
          <div className={styles.groupActions}>
            <Button
              label="Mark All as Read"
              variant="text"
              isDisabled={isMarkAllAsReadPending}
              isPending={isMarkAllAsReadPending}
              onPress={onMarkAllAsRead}
            />
          </div>
        )}
      </div>
      {children}
    </div>
  )
}

export interface NotificationItemProps {
  actionUrl: string | null
  onPress: () => unknown
  imageSource: string | null | undefined
  text: string
  plainText: string
  dispatchedAt: Date
  workspaceName: string
  isRead: boolean
}

export function NotificationItem({
  actionUrl,
  onPress,
  imageSource,
  text,
  plainText,
  dispatchedAt,
  workspaceName,
  isRead,
}: NotificationItemProps) {
  return (
    <Link className={styles.item} to={actionUrl || undefined} onClick={onPress}>
      <div className={styles.imageContainer}>
        <Avatar
          size={32}
          subject={{
            avatar: imageSource || logoWorklayerBlueUrl,
          }}
        />
      </div>
      <div className={styles.content}>
        <p className={styles.title} title={plainText}>
          <MinidownContent value={text} />
        </p>
        <p className={styles.subtitle}>
          {timeago(dispatchedAt)} &bull; {workspaceName}
        </p>
      </div>
      <div className={styles.meta}>
        {!isRead && <div className={styles.unread} />}
      </div>
    </Link>
  )
}
